import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../api/axios";
import EditIcon from "@mui/icons-material/Edit";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import PageShell, { SectionCard } from "components/patch/PageShell";

export default function StaffList() {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await axios.get("/staff");
        setStaff(response.data?.data || []);
      } catch (error) {
        console.error("Error fetching staff:", error);
        setStaff([]);
      }
    };

    fetchStaff();
  }, []);

  const filteredStaff = useMemo(() => {
    return [...staff]
      .filter((s) => {
        const searchText = search.toLowerCase();
        return (
          s.pf_no?.toLowerCase().includes(searchText) ||
          s.biodata?.surname?.toLowerCase().includes(searchText) ||
          s.biodata?.firstname?.toLowerCase().includes(searchText) ||
          s.biodata?.othername?.toLowerCase().includes(searchText)
        );
      })
      .sort((a, b) => {
        const aMissing = [a.biodata?.surname, a.biodata?.firstname].filter(
          (v) => !v || v === "-"
        ).length;
        const bMissing = [b.biodata?.surname, b.biodata?.firstname].filter(
          (v) => !v || v === "-"
        ).length;

        return aMissing - bMissing;
      });
  }, [staff, search]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditOpen = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      surname: staffMember.biodata?.surname || "",
      firstname: staffMember.biodata?.firstname || "",
      othername: staffMember.biodata?.othername || "",
      title: staffMember.biodata?.title?.name || "",
      gender: staffMember.biodata?.gender?.name || "",
      employment_status: staffMember.employment_status || "",
      grade_level: staffMember.grade_level || "",
      step: staffMember.step || "",
    });
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
    setSelectedStaff(null);
    setFormData({});
  };

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpdate = async () => {
    if (!selectedStaff?.pf_no) {
      return;
    }

    setSaving(true);

    try {
      const pf_no = selectedStaff.pf_no;
      await axios.put(`/staff/${pf_no}`, formData);

      setStaff((prev) =>
        prev.map((s) =>
          s.pf_no === pf_no
            ? {
                ...s,
                employment_status: formData.employment_status,
                grade_level: formData.grade_level,
                step: formData.step,
                biodata: {
                  ...s.biodata,
                  surname: formData.surname,
                  firstname: formData.firstname,
                  othername: formData.othername,
                  title: {
                    ...(s.biodata?.title || {}),
                    name: formData.title,
                  },
                  gender: {
                    ...(s.biodata?.gender || {}),
                    name: formData.gender,
                  },
                },
              }
            : s
        )
      );

      handleEditClose();
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell title="Edit Staff" subtitle="Search staff records and update employee information.">
      <SectionCard title="Search and Review" subtitle="Locate staff and open quick edit.">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <MDInput
              label="Search staff by PF No or name"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <Box mt={3}>
        <SectionCard title="Staff Records">
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "18px",
              border: "1px solid #f1eaea",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>S/N</TableCell>
                  <TableCell>PF No</TableCell>
                  <TableCell>Surname</TableCell>
                  <TableCell>Firstname</TableCell>
                  <TableCell>Othername</TableCell>
                  <TableCell align="center">Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStaff
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((s, index) => (
                    <TableRow key={s.pf_no} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{s.pf_no}</TableCell>
                      <TableCell>{s.biodata?.surname || "-"}</TableCell>
                      <TableCell>{s.biodata?.firstname || "-"}</TableCell>
                      <TableCell>{s.biodata?.othername || "-"}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Staff">
                          <IconButton onClick={() => handleEditOpen(s)} color="error">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredStaff.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </SectionCard>
      </Box>

      <Dialog open={openEdit} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Staff Details</DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Surname"
                name="surname"
                fullWidth
                value={formData.surname || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Firstname"
                name="firstname"
                fullWidth
                value={formData.firstname || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Othername"
                name="othername"
                fullWidth
                value={formData.othername || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Title"
                name="title"
                fullWidth
                value={formData.title || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Gender"
                name="gender"
                fullWidth
                value={formData.gender || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Employment Status"
                name="employment_status"
                fullWidth
                value={formData.employment_status || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Grade Level"
                name="grade_level"
                fullWidth
                value={formData.grade_level || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Step"
                name="step"
                fullWidth
                value={formData.step || ""}
                onChange={handleFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton variant="outlined" color="dark" onClick={handleEditClose}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleUpdate} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
